<template>
  <section class="nx-qualifications" id="qualifications">
    <div class="nx-qualifications-container">
      <!-- 标题区 -->
      <div class="nx-qualifications-header nx-animate-on-scroll">
        <p class="nx-qualifications-subtitle">{{ config?.qualifications?.subtitle }}</p>
        <h2 class="nx-qualifications-title">{{ config?.qualifications?.title }}</h2>
        <p class="nx-qualifications-desc">{{ config?.qualifications?.description }}</p>
      </div>

      <!-- PC端查看更多按钮 -->
      <div class="nx-qualifications-more nx-pc-only">
        <a href="javascript:void(0)" class="nx-qualifications-more-btn" @click="goToQualificationsPage">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 17L18 12L13 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6 17L11 12L6 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </div>

      <!-- Tab 切换 -->
      <div class="nx-qualifications-tabs nx-animate-on-scroll">
        <button
          v-for="(tab, key) in tabs"
          :key="key"
          class="nx-qualifications-tab"
          :class="{ 'nx-tab-active': activeTab === key }"
          @click="activeTab = key"
        >
          <span class="nx-tab-text">{{ tab.title }}</span>
        </button>
      </div>

      <!-- 资质内容 -->
      <div class="nx-qualifications-content nx-animate-on-scroll">
        <div
          v-for="categoryItem in currentCategoryList"
          :key="categoryItem.key"
          class="nx-qualification-row"
        >
          <div class="nx-qualification-year-label">
            <span class="nx-qualification-year-text">{{ categoryItem.title }}</span>
          </div>
          <div class="nx-qualification-items">
            <div
              v-for="(item, index) in categoryItem.items"
              :key="index"
              class="nx-qualification-item"
              @click="openQualification(item)"
            >
              <div class="nx-qualification-item-content">
                <span class="nx-qualification-arrow">→</span>
                <h4 class="nx-qualification-item-title" :title="item.title">
                  {{ item.title }}<span v-if="!item.image && !item.url" class="pending-label">（实质审查中）</span>
                </h4>
                <p class="nx-qualification-item-desc">{{ item.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 移动端查看更多按钮 -->
        <div v-if="showMoreIndicator" class="nx-mobile-more-indicator">
          <a href="javascript:void(0)" class="nx-mobile-more-btn" @click="goToQualificationsPage">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 17L18 12L13 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 17L11 12L6 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>查看更多</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'NxQualifications',
  props: {
    config: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      activeTab: 'softwareCopyright',
      scrollObserver: null,
      imageIndex: null,
      isMobile: false,
      maxMobileRows: 5
    };
  },
  computed: {
    tabs() {
      const q = this.config?.qualifications;
      return {
        softwareCopyright: { title: q?.softwareCopyright?.title || '知识产权（软著）', data: q?.softwareCopyright?.categories },
        patent: { title: q?.patent?.title || '知识产权（专利）', data: q?.patent?.categories }
      };
    },
    currentCategoryList() {
      const categories = this.tabs[this.activeTab]?.data || {};
      let categoryList = Object.keys(categories)
        .map(key => ({ key, title: categories[key].title, items: categories[key].items || [] }));

      // 移动端限制显示5行
      if (this.isMobile) {
        categoryList = this.limitToCategories(categoryList, this.maxMobileRows);
      }

      return categoryList;
    },
    showMoreIndicator() {
      // 只在移动端且数据被限制时显示
      if (!this.isMobile) return false;

      const categories = this.tabs[this.activeTab]?.data || {};
      const totalItems = Object.values(categories).reduce((sum, cat) => sum + (cat.items?.length || 0), 0);

      // 计算当前显示的项数
      const displayedItems = this.currentCategoryList.reduce((sum, catItem) => sum + catItem.items.length, 0);

      return displayedItems < totalItems;
    }
  },
  mounted() {
    this.initScrollAnimation();
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    this.cleanupScrollAnimation();
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    limitToCategories(categoryList, maxRows) {
      // 限制显示的资质项总数为maxRows个
      const result = [];
      let count = 0;

      for (const categoryItem of categoryList) {
        if (count >= maxRows) break;

        const remainingItems = categoryItem.items.slice(0, maxRows - count);
        result.push({
          key: categoryItem.key,
          title: categoryItem.title,
          items: remainingItems
        });

        count += remainingItems.length;
      }

      return result;
    },
    goToQualificationsPage() {
      // 高亮父级菜单（关于文信）
      if (this.$parent && this.$parent.$refs && this.$parent.$refs.nav) {
        this.$parent.$refs.nav.activeIndex = '2';
      }
      const url = window.location.origin + '/qualifications';
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    getTabIcon(key) {
      const icons = {
        company: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21h18M5 21V7l8-4 8 4v14M8 21v-2a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>`,
        softwareCopyright: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7H11V5.73C10.4 5.39 10 4.74 10 4a2 2 0 0 1 2-2z"/><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/></svg>`,
        patent: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/><path d="M9 12h6"/></svg>`
      };
      return icons[key] || '';
    },
    openQualification(item) {
      // 优先处理外部链接
      if (item.url) {
        window.open(item.url, '_blank', 'noopener,noreferrer');
        return;
      }
      // 发送事件给父组件，由父组件处理图片查看
      // 优先使用配置的image路径
      let imagePath = item.image;
      if (!imagePath) {
        // 如果没有配置image，则尝试智能匹配
        imagePath = this.findMatchingImage(item.title);
      }
      if (imagePath) {
        this.$emit('open-qualification', imagePath);
      }
    },
    findMatchingImage(title) {
      // 简化版本：直接返回配置的路径或null
      // 实际的图片匹配逻辑可以在父组件中处理
      return null;
    },
    initScrollAnimation() {
      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.15
      };

      this.scrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('nx-visible');
            this.scrollObserver.unobserve(entry.target);
          }
        });
      }, observerOptions);

      this.$nextTick(() => {
        const animatedElements = this.$el.querySelectorAll('.nx-animate-on-scroll');
        animatedElements.forEach(el => {
          this.scrollObserver.observe(el);
        });
      });
    },
    cleanupScrollAnimation() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
      }
    }
  }
};
</script>

<style scoped>
.nx-qualifications {
  position: relative;
  padding: 80px 40px;
  background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%);
  z-index: 1;
}

.nx-qualifications::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 900px;
  height: 900px;
  background:
    radial-gradient(circle, rgba(59, 130, 246, 0.04) 0%, transparent 70%),
    radial-gradient(circle at 70% 30%, rgba(139, 92, 246, 0.03) 0%, transparent 50%);
  border-radius: 50%;
  pointer-events: none;
  opacity: 0.7;
}

.nx-qualifications-container {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.nx-qualifications-header {
  text-align: center;
  margin-bottom: 50px;
}

.nx-qualifications-subtitle {
  font-size: 1.2rem;
  color: var(--nx-blue-primary);
  font-weight: 500;
  letter-spacing: 0.3rem;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
  text-shadow: 0 0 20px rgba(25, 118, 210, 0.4);
}

.nx-qualifications-title {
  font-size: clamp(32px, 4.5vw, 48px);
  font-weight: 800;
  color: var(--nx-text-title);
  margin-bottom: 12px;
  text-shadow:
    0 0 30px rgba(13, 71, 161, 0.15),
    0 0 60px rgba(59, 130, 246, 0.1);
  position: relative;
  display: inline-block;
}

.nx-qualifications-title::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg,
    transparent,
    var(--nx-blue-primary),
    transparent);
  border-radius: 2px;
  box-shadow: 0 0 15px rgba(59, 130, 246, 0.6);
  animation: titleGlow 2s ease-in-out infinite;
}

.nx-qualifications-desc {
  font-size: 17px;
  color: var(--nx-text-desc);
}

/* 查看更多按钮 */
.nx-qualifications-more {
  position: absolute;
  top: 190px;
  right: 40px;
  z-index: 10;
}

.nx-qualifications-more-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  border: 1.5px solid rgba(25, 118, 210, 0.2);
  color: #1976d2;
  text-decoration: none;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow:
    0 2px 8px rgba(25, 118, 210, 0.08),
    inset 0 1px 2px rgba(255, 255, 255, 0.8);
  cursor: pointer;
}

.nx-qualifications-more-btn:hover {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  border-color: transparent;
  color: #fff;
  transform: translateX(4px);
  box-shadow:
    0 4px 15px rgba(25, 118, 210, 0.25),
    inset 0 0 0 transparent;
}

.nx-qualifications-more-btn svg {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.nx-qualifications-more-btn:hover svg {
  transform: translateX(2px);
}

/* Tab 切换 */
.nx-qualifications-tabs {
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-bottom: 50px;
  flex-wrap: wrap;
}

.nx-qualifications-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 160px;
  padding: 14px 24px;
  background: var(--nx-blue-primary);
  border: none;
  border-radius: 0;
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: 0.7;
  text-align: center;
}

.nx-qualifications-tab:hover {
  opacity: 0.9;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.3);
}

.nx-qualifications-tab.nx-tab-active {
  opacity: 1;
  box-shadow: 0 6px 25px rgba(21, 101, 192, 0.4);
  background: linear-gradient(135deg, #42a5f5 0%, #1976d2 100%);
  font-weight: 600;
  font-size: 17px;
  transform: translateY(-2px);
}

.nx-tab-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nx-tab-icon svg {
  width: 100%;
  height: 100%;
}

.nx-tab-text {
  white-space: nowrap;
}

/* 资质内容 */
.nx-qualifications-content {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.nx-qualification-row {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.nx-qualification-year-label {
  min-width: 120px;
  max-width: 150px;
  flex-shrink: 0;
  text-align: right;
  padding-top: 15px;
  position: relative;
}

.nx-qualification-year-text {
  font-size: 16px;
  font-weight: 700;
  color: var(--nx-blue-primary);
  text-shadow: 0 2px 8px rgba(21, 101, 192, 0.2);
  display: inline-block;
  position: relative;
  white-space: nowrap;
}

.nx-qualification-year-text::after {
  content: '';
  position: absolute;
  top: -12px;
  left: 0;
  width: 100%;
  height: 1px;
  background: var(--nx-blue-primary);
}

.nx-qualification-items {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  position: relative;
}

.nx-qualification-items::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: rgba(21, 101, 192, 0.2);
}

.nx-qualification-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 15px;
  margin-top: 15px;
  background: rgba(21, 101, 192, 0.05);
  border: 1px solid rgba(21, 101, 192, 0.15);
  border-radius: 4px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.nx-qualification-item:hover {
  background: rgba(21, 101, 192, 0.1);
  border-color: rgba(21, 101, 192, 0.3);
}

.nx-qualification-item-content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.nx-qualification-arrow {
  color: rgba(21, 101, 192, 0.5);
  font-size: 16px;
  flex-shrink: 0;
}

.nx-qualification-item-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--nx-text-title);
  margin: 0;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pending-label {
  color: #f57c00;
  font-weight: 500;
  margin-left: 4px;
  flex-shrink: 0;
}

.nx-qualification-item:hover .nx-qualification-item-title {
  color: var(--nx-blue-primary);
}

.nx-qualification-item-desc {
  font-size: 13px;
  line-height: 1.6;
  color: var(--nx-text-desc);
  margin: 0;
}

/* 入场动画 */
.nx-animate-on-scroll {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
}

.nx-animate-on-scroll.nx-visible {
  opacity: 1;
  transform: translateY(0);
}

/* 标题光效动画 */
@keyframes titleGlow {
  0%, 100% {
    width: 60px;
    opacity: 0.6;
  }
  50% {
    width: 120px;
    opacity: 1;
  }
}

/* 响应式 */
@media (max-width: 768px) {
  .nx-qualifications {
    padding: 60px 20px 50px;
  }

  .nx-qualifications-header {
    margin-bottom: 50px;
  }

  .nx-qualifications-more {
    position: static;
    text-align: center;
    margin: -20px 0 30px;
  }

  /* PC端查看更多按钮在移动端隐藏 */
  .nx-pc-only {
    display: none !important;
  }

  /* 移动端查看更多按钮 - 使用原有样式 */
  .nx-mobile-more-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 18px;
    background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
    border: 1.5px solid rgba(25, 118, 210, 0.2);
    color: #1976d2;
    text-decoration: none;
    border-radius: 10px;
    transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    box-shadow:
      0 2px 8px rgba(25, 118, 210, 0.08),
      inset 0 1px 2px rgba(255, 255, 255, 0.8);
    cursor: pointer;
    font-size: 14px;
  }

  .nx-mobile-more-btn:active {
    background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
    border-color: transparent;
    color: #fff;
  }

  .nx-mobile-more-btn svg {
    width: 18px;
    height: 18px;
  }

  .nx-qualifications-tabs {
    gap: 10px;
  }

  .nx-qualifications-tab {
    padding: 12px 24px;
    font-size: 14px;
  }

  /* 移动端隐藏tab图标 */
  .nx-tab-icon {
    display: none;
  }

  .nx-qualification-row {
    flex-direction: column;
    gap: 15px;
  }

  .nx-qualification-year-label {
    width: 100%;
    text-align: left;
    padding-top: 0;
    padding-left: 10px;
  }

  .nx-qualification-year-text {
    font-size: 14px;
  }

  .nx-qualification-items {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .nx-qualification-item {
    padding: 12px;
    min-width: 0;
  }

  .nx-qualification-item-content {
    width: 100%;
    min-width: 0;
  }

  .nx-qualification-item-title {
    font-size: 14px;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .nx-qualification-item-desc {
    font-size: 12px;
  }

  /* 移动端更多提示 */
  .nx-mobile-more-indicator {
    text-align: center;
    padding: 20px 0 10px;
  }
}
</style>
